import { type Languages_userProfile$key } from '#app/__generated__/relay/Languages_userProfile.graphql.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { PROFESSIONAL_LANGUAGE_LABELS } from '#app/features/workPreferences/constants.js';
import { type WorkPreferenceLanguages } from '#app/features/workPreferences/types.js';
import { InlineEditingEmptyPlaceholder } from '../InlineEditingEmptyPlaceholder.js';
import { EditIcon, LanguageIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { lazy, Suspense, useState } from 'react';
import { styled } from 'styled-components';

const LazyLanguagesInlineEditing = lazy(async () => {
  return {
    default: (await import('./LanguagesInlineEditing.js'))
      .LanguagesInlineEditing,
  };
});

const LanguagesLayout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;

  > svg {
    flex-shrink: 0;
  }
`;

const getLanguagesString = (languages: WorkPreferenceLanguages) => {
  return languages
    .map((language) => PROFESSIONAL_LANGUAGE_LABELS[language.languageName])
    .join(' • ');
};

export const Languages = ({
  userProfileRef,
}: {
  readonly userProfileRef: Languages_userProfile$key;
}) => {
  const data = useFragment(
    graphql`
      fragment Languages_userProfile on UserProfile {
        workPreferences {
          languages {
            languageName
            proficiency
          }
        }
        visitorCanEdit
      }
    `,
    userProfileRef,
  );

  const { visitorCanEdit, workPreferences } = data;

  const [isInlineEditing, setIsInlineEditing] = useState(false);

  const initialLanguages: WorkPreferenceLanguages =
    workPreferences.languages.map((language) => ({
      languageName: language.languageName,
      proficiency: language.proficiency,
    }));

  const languageIcon = <LanguageIcon size={16} />;

  const visitorCanEditDefaultState =
    initialLanguages.length > 0 ? (
      <InlineEditContainer
        label="Edit your languages"
        onClick={() => setIsInlineEditing(true)}
        visitorCanEdit={visitorCanEdit}
      >
        <LanguagesLayout>
          {languageIcon}
          {getLanguagesString(initialLanguages)}
        </LanguagesLayout>
        <EditIcon size={16} />
      </InlineEditContainer>
    ) : (
      <InlineEditingEmptyPlaceholder
        label="Add languages"
        onClick={() => {
          setIsInlineEditing(true);
        }}
      />
    );

  if (visitorCanEdit) {
    if (isInlineEditing) {
      return (
        <Suspense fallback={visitorCanEditDefaultState}>
          <LazyLanguagesInlineEditing
            languages={initialLanguages}
            setIsInlineEditing={setIsInlineEditing}
          />
        </Suspense>
      );
    }

    return visitorCanEditDefaultState;
  }

  if (initialLanguages.length > 0) {
    return (
      <LanguagesLayout>
        {languageIcon}
        {getLanguagesString(initialLanguages)}
      </LanguagesLayout>
    );
  }

  return null;
};
